// Staff "Partner Brands" EDIT form (single page) at /partners/brands/:id.
// Slug is read-only here (issued /prequalify/{slug} links must stay stable) —
// including across a RELINK: changing the owner never rewrites the slug (the
// update request doesn't even carry one).
// The CREATE flow now lives in the multi-step PartnerBrandCreateWizard; this
// page shares the logo / colors / contact field groups with it so the two
// surfaces never drift.
// Gated by Permissions.Partners.ManageBrands (route-level in App.tsx).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type {
  PartnerBrand,
  PartnerBrandColors,
  PartnerBrandValidationMessage,
  PartnerLogoMode,
} from "@/api/partnerBrands";
import {
  usePartnerBrand,
  useCreatePartnerBrand,
  useUpdatePartnerBrand,
} from "@/hooks/partners/usePartnerBrands";
import {
  Button,
  Input,
  Label,
  Textarea,
  useDocumentTitle,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  DEFAULT_COLORS,
  DEFAULT_FONT_BODY,
  DEFAULT_FONT_DISPLAY,
  asSlugConflict,
  asValidationResult,
  toSlug,
} from "@/components/partners/partnerBrandForm/shared";
import { PartnerBrandColorsSection } from "@/components/partners/partnerBrandForm/PartnerBrandColorsSection";
import { PartnerBrandContactSection } from "@/components/partners/partnerBrandForm/PartnerBrandContactSection";
import { PartnerBrandLogoControl } from "@/components/partners/partnerBrandForm/PartnerBrandLogoControl";
import {
  OWNERLESS,
  PartnerBrandOwnerSection,
  ownerFromBrand,
  ownerIds,
  type PartnerBrandOwner,
} from "@/components/partners/partnerBrandForm/PartnerBrandOwnerSection";

interface FormState {
  slug: string;
  displayName: string;
  referenceCode: string;
  logoMode: PartnerLogoMode;
  wordmarkText: string;
  logoAssetPath: string;
  logoUrl: string;
  /** Preview URLs for the two background-specific variants. Display only —
   *  they have no field on the update payload (their upload/delete endpoints
   *  are their only writer), so saving this form can never clear them. */
  logoUrlOnLight: string;
  logoUrlOnDark: string;
  colors: PartnerBrandColors;
  fontDisplay: string;
  fontBody: string;
  heroHeadline: string;
  heroSubcopy: string;
  contactCity: string;
  contactPhone: string;
  contactWebsite: string;
  contactEmail: string;
  // No isActive here BY DESIGN — publishing is not part of this form. See the
  // read-only publish-state note in the markup below.
  analysisDocsEnabled: boolean;
  /** Broker | brokerage | ownerless — one value, so the two owner columns can
   *  never both be set from this form. */
  owner: PartnerBrandOwner;
}

const EMPTY: FormState = {
  slug: "",
  displayName: "",
  referenceCode: "",
  logoMode: "WordmarkText",
  wordmarkText: "",
  logoAssetPath: "",
  logoUrl: "",
  logoUrlOnLight: "",
  logoUrlOnDark: "",
  colors: DEFAULT_COLORS,
  fontDisplay: DEFAULT_FONT_DISPLAY,
  fontBody: DEFAULT_FONT_BODY,
  heroHeadline: "",
  heroSubcopy: "",
  contactCity: "",
  contactPhone: "",
  contactWebsite: "",
  contactEmail: "",
  // This blank form IS the create path (edit hydrates over it from the loaded
  // brand), so this default decides whether a new site ships with the analysis
  // documents on. It does, now that the AI-disclosure wording is signed off.
  analysisDocsEnabled: true,
  owner: OWNERLESS,
};

export function PartnerBrandEditPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  useDocumentTitle(isEdit ? "Edit partner brand" : "New partner brand");
  const navigate = useNavigate();
  const toast = useToast();

  const detail = usePartnerBrand(id);
  const create = useCreatePartnerBrand();
  const update = useUpdatePartnerBrand(id ?? "");

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [messages, setMessages] = useState<PartnerBrandValidationMessage[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // Hydrate the form once the edited brand loads (create starts from EMPTY).
  useEffect(() => {
    if (isEdit && detail.data && !hydrated) {
      const b = detail.data;
      setForm({
        slug: b.slug,
        displayName: b.displayName,
        referenceCode: b.referenceCode,
        logoMode: b.logoMode,
        wordmarkText: b.wordmarkText ?? "",
        logoAssetPath: b.logoAssetPath ?? "",
        logoUrl: b.logoUrl ?? "",
        logoUrlOnLight: b.logoUrlOnLight ?? "",
        logoUrlOnDark: b.logoUrlOnDark ?? "",
        colors: b.colors,
        fontDisplay: b.fontDisplay,
        fontBody: b.fontBody,
        heroHeadline: b.heroHeadline ?? "",
        heroSubcopy: b.heroSubcopy ?? "",
        contactCity: b.contactCity ?? "",
        contactPhone: b.contactPhone ?? "",
        contactWebsite: b.contactWebsite ?? "",
        contactEmail: b.contactEmail ?? "",
        analysisDocsEnabled: b.analysisDocsEnabled,
        owner: ownerFromBrand(b),
      });
      setHydrated(true);
    }
  }, [isEdit, detail.data, hydrated]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setColor = (key: keyof PartnerBrandColors, value: string) =>
    setForm((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));

  // The shared logo control uploads/removes immediately and hands back the
  // updated brand; sync the fields it touches so a subsequent save preserves
  // the uploaded logo (and the hydrate guard doesn't overwrite it).
  const handleLogoChange = (brand: PartnerBrand) =>
    setForm((prev) => ({
      ...prev,
      logoMode: brand.logoMode,
      logoAssetPath: brand.logoAssetPath ?? "",
      wordmarkText: brand.wordmarkText ?? prev.wordmarkText,
      logoUrl: brand.logoUrl ?? "",
      logoUrlOnLight: brand.logoUrlOnLight ?? "",
      logoUrlOnDark: brand.logoUrlOnDark ?? "",
    }));

  const toPayload = () => ({
    displayName: form.displayName.trim(),
    referenceCode: form.referenceCode.trim(),
    logoMode: form.logoMode,
    wordmarkText: form.wordmarkText.trim() || null,
    logoAssetPath: form.logoAssetPath.trim() || null,
    colors: form.colors,
    fontDisplay: form.fontDisplay.trim(),
    fontBody: form.fontBody.trim(),
    heroHeadline: form.heroHeadline.trim() || null,
    heroSubcopy: form.heroSubcopy.trim() || null,
    contactCity: form.contactCity.trim() || null,
    contactPhone: form.contactPhone.trim() || null,
    contactWebsite: form.contactWebsite.trim() || null,
    contactEmail: form.contactEmail.trim() || null,
    analysisDocsEnabled: form.analysisDocsEnabled,
    // Relink — attach, reassign, or clear back to ownerless. Slug untouched.
    ...ownerIds(form.owner),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setMessages([]);
    if (!form.displayName.trim()) return setFormError("Display name is required.");
    if (!form.referenceCode.trim()) return setFormError("Reference code is required.");
    if (!isEdit && !form.slug.trim()) return setFormError("Slug is required.");
    // A kind chosen with no record picked is a HALF-FINISHED selection, and
    // ownerIds() serializes it as both-null — indistinguishable on the wire from
    // a deliberate "make this ownerless". Refuse it: never guess the previous
    // owner, and never write an ownership change the staff member didn't make.
    // Going ownerless stays available, explicitly, via the "No owner" option.
    if (form.owner.kind !== "none" && !form.owner.id) {
      return setFormError(
        `Choose an owning ${form.owner.kind === "broker" ? "broker" : "brokerage"}, or select "No owner".`,
      );
    }

    try {
      if (isEdit) {
        await update.mutateAsync(toPayload());
        toast.success("Partner brand saved.");
        navigate("/partners/brands");
      } else {
        const created = await create.mutateAsync({ slug: form.slug.trim(), ...toPayload() });
        toast.success("Partner brand created.");
        navigate(`/partners/brands/${created.id}`);
      }
    } catch (err) {
      const validation = asValidationResult(err);
      if (validation) {
        setMessages(validation.messages);
        setFormError("Fix the contrast errors before saving.");
        return;
      }
      const conflict = asSlugConflict(err);
      if (conflict) {
        setFormError(
          `The slug "${conflict.slug}" is already used by "${conflict.displayName}". Choose a different slug.`,
        );
        return;
      }
      setFormError(extractErrorMessage(err));
    }
  };

  const pending = create.isPending || update.isPending;

  if (isEdit && detail.isLoading) {
    return <div className="text-small text-gray-mid p-space-4">Loading partner brand…</div>;
  }
  if (isEdit && detail.error) {
    return (
      <div className="text-small text-status-error p-space-4">
        {extractErrorMessage(detail.error)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4 p-space-4">
      <PageHeader
        eyebrow="Partners"
        title={isEdit ? form.displayName || "Edit partner brand" : "New partner brand"}
        subtitle={isEdit ? `/prequalify/${form.slug}` : "White-label skin for the public prequalification funnel."}
        actions={
          <Button variant="tertiary" onClick={() => navigate("/partners/brands")}>
            <ArrowLeft className="w-4 h-4 mr-space-1" aria-hidden />
            Back to list
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-space-4 max-w-3xl">
        {/* ── Identity ── */}
        <section className="flex flex-col gap-space-3">
          <h2 className="font-display font-bold text-h3 m-0">Identity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="pb-slug">Slug</Label>
              <Input
                id="pb-slug"
                value={form.slug}
                disabled={isEdit}
                placeholder="andiamo-ventures"
                onChange={(e) => set("slug", toSlug(e.target.value))}
              />
              <p className="text-caption text-gray-mid m-0">
                {isEdit
                  ? "Slug is fixed after create — issued links depend on it."
                  : "Lowercase letters, digits and hyphens. Public URL: /prequalify/{slug}."}
              </p>
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="pb-ref">Reference code</Label>
              <Input
                id="pb-ref"
                value={form.referenceCode}
                maxLength={8}
                placeholder="AND"
                onChange={(e) => set("referenceCode", e.target.value.toUpperCase())}
              />
              <p className="text-caption text-gray-mid m-0">
                Up to 8 chars — stamped into the reference number (PQ-AND-B-2026-0042).
              </p>
            </div>
            <div className="flex flex-col gap-space-1 sm:col-span-2">
              <Label htmlFor="pb-name">Display name</Label>
              <Input
                id="pb-name"
                value={form.displayName}
                placeholder="Andiamo Ventures"
                onChange={(e) => set("displayName", e.target.value)}
                required
              />
            </div>
          </div>
        </section>

        {/* ── Ownership (relink) ── */}
        <PartnerBrandOwnerSection
          value={form.owner}
          onChange={(next) => set("owner", next)}
        />

        {/* ── Logo ── */}
        <section className="flex flex-col gap-space-3">
          <h2 className="font-display font-bold text-h3 m-0">Logo</h2>
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="pb-logo-mode">Logo mode</Label>
            <select
              id="pb-logo-mode"
              className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small w-full sm:w-64"
              value={form.logoMode}
              onChange={(e) => set("logoMode", e.target.value as PartnerLogoMode)}
            >
              <option value="WordmarkText">Wordmark text</option>
              <option value="Asset">Uploaded asset</option>
            </select>
          </div>
          {form.logoMode === "WordmarkText" ? (
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="pb-wordmark">Wordmark text</Label>
              <Input
                id="pb-wordmark"
                value={form.wordmarkText}
                placeholder="Andiamo"
                onChange={(e) => set("wordmarkText", e.target.value)}
              />
              <p className="text-caption text-gray-mid m-0">Rendered in the display font.</p>
            </div>
          ) : (
            <PartnerBrandLogoControl
              brandId={id ?? ""}
              logoUrl={form.logoUrl || null}
              logoUrlOnLight={form.logoUrlOnLight || null}
              logoUrlOnDark={form.logoUrlOnDark || null}
              onChange={handleLogoChange}
            />
          )}
        </section>

        {/* ── Color tokens (+ live WCAG preview) ── */}
        <PartnerBrandColorsSection
          colors={form.colors}
          onColorChange={setColor}
          messages={messages}
          onMessagesChange={setMessages}
        />

        {/* ── Fonts ── */}
        <section className="flex flex-col gap-space-3">
          <h2 className="font-display font-bold text-h3 m-0">Fonts</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="pb-font-display">Display font</Label>
              <Input
                id="pb-font-display"
                value={form.fontDisplay}
                onChange={(e) => set("fontDisplay", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="pb-font-body">Body font</Label>
              <Input
                id="pb-font-body"
                value={form.fontBody}
                onChange={(e) => set("fontBody", e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* ── Hero copy ── */}
        <section className="flex flex-col gap-space-3">
          <h2 className="font-display font-bold text-h3 m-0">Hero copy</h2>
          <p className="text-caption text-gray-mid m-0">
            Optional — falls back to the Phoenix defaults when blank.
          </p>
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="pb-hero-headline">Headline</Label>
            <Input
              id="pb-hero-headline"
              value={form.heroHeadline}
              onChange={(e) => set("heroHeadline", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="pb-hero-subcopy">Subcopy</Label>
            <Textarea
              id="pb-hero-subcopy"
              value={form.heroSubcopy}
              rows={2}
              onChange={(e) => set("heroSubcopy", e.target.value)}
            />
          </div>
        </section>

        {/* ── Contact ── */}
        <section className="flex flex-col gap-space-3">
          <h2 className="font-display font-bold text-h3 m-0">Point of contact</h2>
          <PartnerBrandContactSection
            city={form.contactCity}
            phone={form.contactPhone}
            website={form.contactWebsite}
            email={form.contactEmail}
            onCityChange={(v) => set("contactCity", v)}
            onPhoneChange={(v) => set("contactPhone", v)}
            onWebsiteChange={(v) => set("contactWebsite", v)}
            onEmailChange={(v) => set("contactEmail", v)}
          />
        </section>

        {/* ── Publish state — READ-ONLY here, deliberately ──
            There used to be an editable "Active" checkbox in this spot. It could
            never work: UpdatePartnerBrandRequest is Omit<Create…, "slug" |
            "activate">, so the form had nowhere to put the flag and toPayload()
            never sent one. The control accepted the click, reported "Partner
            brand saved.", and discarded it — so a staff member unpublishing a
            live partner site here would believe they had.
            Publishing has its own endpoint (POST {id}/active) and its own
            surface, so this states the current value and points there rather
            than re-implementing a second writer for it. */}
        {isEdit && detail.data && (
          <p className="text-caption text-gray-mid m-0">
            This site is currently{" "}
            <span className="font-semibold text-onyx">
              {detail.data.isActive ? "published" : "unpublished"}
            </span>
            {detail.data.isActive
              ? "."
              : " — the public link falls back to the Phoenix funnel."}{" "}
            Publishing is a separate action: use Publish / Unpublish on the
            Prequalification tab of the owning broker or brokerage record.
          </p>
        )}

        {/* ── Private analysis documents ── */}
        <label className="flex items-center gap-space-2 text-small">
          <input
            type="checkbox"
            checked={form.analysisDocsEnabled}
            onChange={(e) => set("analysisDocsEnabled", e.target.checked)}
          />
          Enable private analysis documents for this partner's prequalification
          submissions
        </label>

        {formError && <p className="text-small text-status-error m-0">{formError}</p>}

        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="tertiary" onClick={() => navigate("/partners/brands")}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create partner brand"}
          </Button>
        </div>
      </form>
    </div>
  );
}
